import React from "react";
import { Avatar, Box, Button, Card, Typography, Divider } from "@mui/material";
import PersonIcon from "@mui/icons-material/Person";
import { useSelector } from "react-redux";

import { RootState } from "../../../../../store/Index";
import { BookingData } from "../../../../../types/bookings";

// ── Helpers ──────────────────────────────────────────────────────────────────

function formatDate(val?: string | null): string {
  if (!val) return "-";
  const [y, m, d] = val.split("-");
  return d && m && y ? `${d}/${m}/${y}` : val;
}

function formatTime(val?: string | null): string {
  return val || "-";
}

function capitalize(val?: string | null): string {
  if (!val) return "-";
  return val.charAt(0).toUpperCase() + val.slice(1).replace(/_/g, " ");
}

// ── Sub-components ────────────────────────────────────────────────────────────

function PreviewSection({
  title,
  children,
  color800,
}: {
  title: string;
  children: React.ReactNode;
  color800: string;
}) {
  return (
    <Card
      elevation={0}
      sx={{
        borderRadius: 3,
        p: 2.5,
        backgroundColor: color800,
        border: "1px solid rgba(255,255,255,0.05)",
        display: "flex",
        flexDirection: "column",
        gap: 1.5,
      }}
    >
      <Typography
        variant="caption"
        color="text.secondary"
        fontWeight={700}
        letterSpacing="0.05em"
        sx={{ textTransform: "uppercase", opacity: 0.8 }}
      >
        {title}
      </Typography>
      <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
        {children}
      </Box>
    </Card>
  );
}

function PreviewItem({
  label,
  value,
  accent,
}: {
  label: string;
  value: string;
  accent?: string;
}) {
  return (
    <Box
      sx={{
        display: "flex",
        justifyContent: "space-between",
        alignItems: "flex-start",
        py: 0.5,
      }}
    >
      <Typography variant="body2" color="text.secondary">
        {label}
      </Typography>
      <Typography
        variant="body2"
        fontWeight={500}
        sx={{
          maxWidth: "65%",
          textAlign: "right",
          color: accent ?? "text.primary",
        }}
      >
        {value || "-"}
      </Typography>
    </Box>
  );
}

// ── PreviewPanel ──────────────────────────────────────────────────────────────

export function PreviewPanel({ formData }: { formData: BookingData }) {
  const reduxTheme = useSelector((state: RootState) => state.theme);

  const color10 = reduxTheme.primary_colors?.[10] ?? "#fff";
  const color400 = reduxTheme.primary_colors?.[400] ?? "#724E41";
  const color800 = reduxTheme.primary_colors?.[800] ?? "#1A1A1A";
  const color900 = reduxTheme.primary_colors?.[900] ?? "#121212";

  const isConference =
    formData.booking_type === "CONFERENCE" ||
    formData.booking_type === "conference";

  // ── Derived display values ────────────────────────────────────────────────

  const clientName = [
    formData.client_salutation,
    formData.first_name,
    formData.surname,
  ]
    .filter(Boolean)
    .join(" ");

  const deceasedName = [
    formData.deceased_salutation,
    formData.deceased_name,
    formData.deceased_surname,
  ]
    .filter(Boolean)
    .join(" ");

  const venueAddress = [formData.address_one, formData.address_two]
    .filter(Boolean)
    .join(", ");

  const serviceDateTime = formData.date_of_service
    ? `${formatDate(formData.date_of_service)}  ${formatTime(formData.start_time)} – ${formatTime(formData.end_time)}`
    : "-";

  const conferenceDateRange =
    formData.start_date_of_event && formData.end_date_of_event
      ? `${formatDate(formData.start_date_of_event)} – ${formatDate(formData.end_date_of_event)}`
      : formData.start_date_of_event
        ? formatDate(formData.start_date_of_event)
        : "-";

  const additionalServiceNames =
    formData.additional_services && formData.additional_services.length > 0
      ? formData.additional_services.map((s) => s.label).join(", ")
      : null;

  // ── Render ───────────────────────────────────────────────────────────────

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        height: "100%",
        backgroundColor: color900,
        borderRadius: 4,
        overflow: "hidden",
        boxShadow: "0 4px 24px rgba(0,0,0,0.10)",
      }}
    >
      {/* ── Header ── */}
      <Box
        sx={{
          px: 3,
          height: 120,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          background: `linear-gradient(135deg, ${color400} 0%, ${color10} 100%)`,
          flexShrink: 0,
        }}
      >
        <Typography variant="subtitle1" color="white" fontWeight={700}>
          Booking Information
        </Typography>

        <Button
          variant="outlined"
          size="small"
          sx={{
            color: "#fff",
            borderColor: "rgba(255,255,255,0.8)",
            borderRadius: "20px",
            textTransform: "none",
            fontSize: 12,
            px: 2,
            "&:hover": {
              borderColor: "#fff",
              bgcolor: "rgba(255,255,255,0.15)",
            },
          }}
        >
          Preview
        </Button>
      </Box>

      {/* ── Scrollable body ───────────────────────────────────────────────── */}
      <Box sx={{ p: 3, overflowY: "auto", flex: 1 }}>
        {/* ── Identity block ─────────────────────────────────────────────── */}
        {isConference ? (
          <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 4 }}>
            <Avatar
              sx={{
                width: 80,
                height: 80,
                bgcolor: "transparent",
                color: "rgba(150,150,150,0.7)",
                flexShrink: 0,
              }}
            >
              <PersonIcon sx={{ fontSize: 40 }} />
            </Avatar>

            <Box>
              <Typography variant="subtitle1" fontWeight={700} lineHeight={1.2}>
                {formData.title || "Conference Name"}
              </Typography>

              {conferenceDateRange !== "-" && (
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ mt: 0.5 }}
                >
                  {conferenceDateRange}
                </Typography>
              )}

              {formData.conference_type && (
                <Typography
                  variant="caption"
                  color="text.secondary"
                  display="block"
                >
                  {capitalize(formData.conference_type)}
                </Typography>
              )}
            </Box>
          </Box>
        ) : (
          <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 4 }}>
            <Avatar
              src={formData.photo_url ?? undefined}
              sx={{
                width: 80,
                height: 80,
                bgcolor: "transparent",
                color: "rgba(150,150,150,0.7)",
                flexShrink: 0,
              }}
            >
              <PersonIcon sx={{ fontSize: 40 }} />
            </Avatar>

            <Box>
              <Typography variant="subtitle1" fontWeight={700} lineHeight={1.2}>
                {deceasedName || "Deceased Name"}
              </Typography>

              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ mt: 0.5 }}
              >
                {formatDate(formData.date_of_birth)}
                {" – "}
                {formatDate(formData.date_of_death)}
              </Typography>

              {formData.gender && (
                <Typography
                  variant="caption"
                  color="text.secondary"
                  display="block"
                >
                  {formData.gender}
                </Typography>
              )}
            </Box>
          </Box>
        )}

        {/* ── Preview cards ──────────────────────────────────────────────── */}
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
          {/* Client Information — same for all booking types */}
          {/* ── Preview cards ──────────────────────────────────────────────── */}
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
            {/* Client Information — same for all booking types */}
            <PreviewSection title="Client Information" color800={color800}>
              <PreviewItem label="Name" value={clientName} />
              <PreviewItem label="Phone" value={formData.phone ?? ""} />
              <PreviewItem label="Email" value={formData.email ?? ""} />
            </PreviewSection>

            {/* Service Information */}
            {isConference ? (
              <PreviewSection title="Service Information" color800={color800}>
                <PreviewItem
                  label="Venue"
                  value={formData.venue_name ?? formData.address_one ?? ""}
                />
                <PreviewItem label="Address" value={venueAddress} />
              </PreviewSection>
            ) : (
              <PreviewSection title="Service Information" color800={color800}>
                <PreviewItem
                  label="Venue"
                  value={formData.venue_name ?? formData.address_one ?? ""}
                />
                <PreviewItem label="Address" value={venueAddress} />
                <PreviewItem label="Date & Time" value={serviceDateTime} />
              </PreviewSection>
            )}

            {/* Package Selection — same for all booking types */}
            <PreviewSection title="Package Selection" color800={color800}>
              <PreviewItem
                label="Selected Plan"
                value={formData.package?.name ?? ""}
                accent={color400}
              />

              {formData.package && (
                <>
                  <Divider sx={{ borderColor: "rgba(255,255,255,0.05)" }} />
                  <PreviewItem
                    label="Package Price"
                    value={`$${(formData.package_total ?? formData.package?.price ?? 0).toFixed(2)}`}
                  />
                </>
              )}

              {additionalServiceNames && (
                <>
                  <Divider sx={{ borderColor: "rgba(255,255,255,0.05)" }} />
                  <PreviewItem
                    label="Additional Services"
                    value={additionalServiceNames}
                  />
                  <PreviewItem
                    label="Additional Cost"
                    value={`$${(formData.additional_services_total ?? 0).toFixed(2)}`}
                  />
                </>
              )}

              {(formData.total ?? 0) > 0 && (
                <>
                  <Divider sx={{ borderColor: "rgba(255,255,255,0.08)" }} />
                  <PreviewItem
                    label="Total"
                    value={`$${(formData.total ?? 0).toFixed(2)}`}
                    accent={color400}
                  />
                </>
              )}
            </PreviewSection>
          </Box>
        </Box>
      </Box>
    </Box>
  );
}
